import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, BarChart3, Check, Clock, Filter, Loader2, Lock, Mail, MessageSquare, Pause, Play, Plus, Repeat, Siren, Sparkles, UserPlus, Users, Workflow } from 'lucide-react';
import { AppChrome, BrandLogo } from './AppChrome';
import { SectionIntro } from './SectionIntro';
import { ProductVignette } from './ProductVignette';
type PrimitiveId = 'chats' | 'shared' | 'workflows' | 'pulses' | 'skills';
const PRIMITIVES: Array<{
  id: PrimitiveId;
  label: string;
  icon: React.ElementType;
  copy: string;
}> = [{
  id: 'chats',
  label: 'AI chats',
  icon: MessageSquare,
  copy: 'A familiar chat interface, except it is signed into your tools. Enough to replace most standalone ChatGPT use.'
}, {
  id: 'shared',
  label: 'Team workspaces',
  icon: Users,
  copy: 'Tracks and groups keep work, files, and access together, so a project has one place instead of living in personal accounts.'
}, {
  id: 'workflows',
  label: 'Workflows',
  icon: Workflow,
  copy: 'Turn a process you repeat into defined steps with declared inputs, then run it on demand and see every run.'
}, {
  id: 'pulses',
  label: 'Pulses',
  icon: Repeat,
  copy: 'Work that runs without being asked — on a schedule, reporting into the channel that needs it.'
}, {
  id: 'skills',
  label: 'Skills',
  icon: Sparkles,
  copy: 'Show Giga how your company does something once, and it reuses that method everywhere else.'
}];

/**
 * One row treatment for every list in these screens. A faint warm fill plus a
 * 1px contact shadow gives each row a surface, which is the difference between
 * a product screenshot and an outlined box.
 */
const ROW = 'rounded-xl border border-[#EFEAE1] bg-[#FCFAF6] shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_1px_2px_-1px_rgba(43,33,26,0.06)]';
export function PlatformPillar() {
  const [active, setActive] = useState<PrimitiveId>('chats');
  return <div data-id="element-891" data-mp="93wjxx6yhgn0">
      {/* No eyebrow, and left-aligned to match the co-worker section. The
       "Workspace" label named the section before the headline could say
       anything, and the headline already states the claim. Left-aligning also
       puts the intro on the same edge as the primitives list directly beneath
       it, so the whole section reads down one axis instead of the copy being
       centred above a left-aligned column. */}
      <SectionIntro className="max-w-3xl text-left" data-id="element-892" data-mp="1cof8rgjh5s46">
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-893" data-mp="1hotkp9o75m7h">
          One place to manage
          <br data-id="element-894" data-mp="1lp3ocfoio0j6" />
          AI within your company.
        </h2>
        <p className="mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-895" data-mp="1qjhlamitufpy">
          Let your power users share their skills with other team members. Build automations in plain English. Schedule them with Pulses. Everything managed in one place, that you own, in the cloud.
        </p>
      </SectionIntro>

      <div className="mt-12 grid items-start gap-8 md:mt-16 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-12" data-id="element-896" data-mp="25fp5vny5dj5r">
        {/* Primitives as a list you step through: only the active one carries
         its explanation, so five ideas never shout at once. */}
        <ul className="space-y-1.5" data-id="element-897" data-mp="3fyilb4nyb5e4">
          {PRIMITIVES.map(p => {
          const on = active === p.id;
          return <li key={p.id} data-id="element-898" data-mp="3b1ipdmktfj0q">
                {/* Selection is carried by an orange rail and a warm fill
                 rather than a floating white card — the card read as a second
                 panel competing with the app frame beside it. */}
                <button type="button" onClick={() => setActive(p.id)} aria-current={on ? 'true' : undefined} className={`relative w-full rounded-xl py-4 pl-5 pr-4 text-left transition-[background-color,color] duration-200 ease-out ${on ? 'bg-white shadow-[0_1px_2px_-1px_rgba(43,33,26,0.08)] ring-1 ring-[#EFEAE1]' : 'hover:bg-[#F6F2EA]'}`} data-id="element-899" data-mp="2ss7cgvw2mm3">
                  <span className={`absolute bottom-3 left-0 top-3 w-[3px] rounded-full transition-colors duration-200 ease-out ${on ? 'bg-[#EA580C]' : 'bg-transparent'}`} aria-hidden="true" data-id="element-900" data-mp="qhbe2rzairc2" />
                  <span className="flex items-center gap-2.5" data-id="element-901" data-mp="g0hcf0b0o0of">
                    <p.icon size={17} strokeWidth={1.9} className={on ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-902" data-mp="126wr0fosdx1" />
                    <span className={`font-title text-xl font-bold tracking-tight ${on ? 'text-[#2B211A]' : 'text-[#6B5D52]'}`} data-id="element-903" data-mp="60qh4wemthna">
                      {p.label}
                    </span>
                  </span>
                  {on && <motion.p initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="mt-2.5 pl-[27px] text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-904" data-mp="yow2nldifao6">
                      {p.copy}
                    </motion.p>}
                </button>
              </li>;
        })}
        </ul>

        {/* No app nav on any of the five. The numbered rail to the left is
         already the navigation for this section, so a sidebar inside the frame
         put two columns of it around one screen — and each screen states what
         it is in its own heading anyway. */}
        {/* THE SAME VIGNETTE AS THE CONNECTIONS SCREEN, adapted to a column.
         Five app screens presented as a bordered card on white read as five
         screenshots; the point of the section is that they are five views of
         one system. Dissolving the frame into the page — and letting it run
         past the column on the right, where nothing sits beside it — makes it
         a window onto a larger product instead.
         It can only bleed RIGHT: the numbered rail occupies the left column,
         and both the artwork and the scrim would run over it. The scrim is
         asymmetric for the same reason — it out-reaches the bleed on the
         right, but stops just short of the rail on the left, where a
         white-on-white wash would fade the labels the reader is choosing
         between. */}
        <ProductVignette ground="#FFFFFF" focusY={26} bleedClass="xl:-mr-[7%] 2xl:-mr-[10%]" scrimClass="-left-[3%] -right-[10%] -top-12 bottom-[-12%] xl:-right-[16%]" data-id="element-905" data-mp="319cdk4ogikmp">
        <AppChrome sidebar={false} data-id="element-906" data-mp="3aatq5d2no46n">
          <motion.div key={active} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.24,
            ease: [0.23, 1, 0.32, 1]
          }} className={active === 'chats' ? 'flex h-full flex-col' : 'px-6 py-6 md:px-9 md:py-8'} data-id="element-907" data-mp="3aa21ynbe18qe">
            {active === 'chats' && <ChatsScreen data-id="element-908" data-mp="2rc7nvuvek715" />}
            {active === 'shared' && <SharedScreen data-id="element-909" data-mp="2rd0zpa97xmq5" />}
            {active === 'workflows' && <WorkflowsScreen data-id="element-910" data-mp="1uul61i4tw1jp" />}
            {active === 'pulses' && <PulsesScreen data-id="element-911" data-mp="1uttjsxx4fg63" />}
            {active === 'skills' && <SkillsScreen data-id="element-912" data-mp="1yvetkaeczec2" />}
          </motion.div>
        </AppChrome>
        </ProductVignette>
      </div>
    </div>;
}
function ScreenHead({
  title,
  meta
}: {
  title: string;
  meta: string;
}) {
  // A real app view states what you're looking at and then rules off. The
  // hairline is what stops these reading as floating wireframe blocks.
  return <div className="mb-6 border-b border-[#F1ECE3] pb-4" data-id="element-913" data-mp="1i9wcs05vs2du">
      <h3 className="font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-914" data-mp="1ddfmazb7lbxl">
        {title}
      </h3>
      <p className="mt-1.5 text-[12.5px] tabular-nums text-neutral-500" data-id="element-915" data-mp="18eulaxo4uo83">
        {meta}
      </p>
    </div>;
}

// The answer, as the thing a revenue lead would actually want on screen: which
// accounts, what they're worth, and the one fact that makes each a risk. Prose
// alone made the reader reassemble a table in their head.
const AT_RISK = [{
  account: 'Northwind Labs',
  value: '$84k',
  closes: '12 Sep',
  reason: 'No exec contact in 41 days · 2 tickets open'
}, {
  account: 'Meridian Health',
  value: '$61k',
  closes: '28 Sep',
  reason: 'Champion left in July · no replacement named'
}, {
  account: 'Fairline Group',
  value: '$37k',
  closes: '30 Sep',
  reason: 'Usage down 38% month over month'
}];
export function ChatsScreen() {
  return <div className="flex h-full min-h-[420px] flex-col" data-id="element-916" data-mp="1zkruc8yps3v3">
      {/* Thread header — what this chat is and which group owns it. */}
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[#F1ECE3] px-5 py-3.5 md:px-7" data-id="element-917" data-mp="24hi8gqqykpdv">
        <div className="min-w-0" data-id="element-918" data-mp="1ps2kfiitay1v">
          <h3 className="truncate font-title text-[16px] font-semibold tracking-[-0.01em] text-[#2B211A]" data-id="element-919" data-mp="1ukm1ipgpkbbf">
            Renewal risk in Salesforce
          </h3>
          <p className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-920" data-mp="1gre6entf4xoz">
            <span className="flex size-4 items-center justify-center rounded bg-[#134E4A] text-[7px] font-bold text-white" data-id="element-921" data-mp="1kvc732ye6gk5">
              SL
            </span>
            Sales
            <span className="text-[#D8CFC2]" data-id="element-922" data-mp="1bt2wye71cp7a">·</span>
            Today, 08:42
          </p>
        </div>
        <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-500 sm:inline-flex" data-id="element-923" data-mp="s9dtc6zhvjcf">
          <span className="size-1.5 rounded-full bg-emerald-500" data-id="element-924" data-mp="1271y7pqte78b" />
          8 tools connected
        </span>
      </div>

      {/* Transcript */}
      <div className="flex-1 space-y-5 overflow-hidden px-5 py-5 md:px-7" data-id="element-925" data-mp="x9s3pvokj5dm">
        <div className="flex justify-end" data-id="element-926" data-mp="1b5vs38crgji6">
          <p className="max-w-[80%] rounded-2xl rounded-br-md bg-[#F4F0E9] px-4 py-2.5 text-[13.5px] font-medium text-[#2B211A]" data-id="element-927" data-mp="168dg193g5o5y">
            Which renewals due this quarter look at risk?
          </p>
        </div>

        <div className="flex gap-3" data-id="element-928" data-mp="1fxna2uhupgwp">
          <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-[#2B211A]" data-id="element-929" data-mp="1fyoxzzjytxvu">
            <Sparkles size={13} strokeWidth={2} className="text-white" data-id="element-930" data-mp="1ty7tnsvlbgr9" />
          </span>
          <div className="min-w-0 flex-1 space-y-3.5" data-id="element-931" data-mp="1pxxnaf3vyobv">
            {/* What it read, before what it concluded. */}
            <div className="relative space-y-1.5 pl-3.5 before:absolute before:bottom-1.5 before:left-0 before:top-1.5 before:w-[2px] before:rounded-full before:bg-[#EFEAE1] before:content-['']" data-id="element-932" data-mp="3f9hr0qkguppd">
              {[['salesforce', 'Read 34 opportunities closing this quarter'], ['intercom', 'Checked support volume per account'], ['gmail', 'Scanned 60 days of thread activity']].map(([slug, label]) => <p key={label} className="flex items-center gap-2 text-[12px] text-neutral-500" data-id="element-933" data-mp="20d2i79l6ret">
                  <span className="flex size-[18px] shrink-0 items-center justify-center rounded-md border border-[#EFEAE1] bg-white" data-id="element-934" data-mp="3t7phqgqwsus3">
                    <BrandLogo slug={slug} className="size-[11px]" data-id="element-935" data-mp="3k6hy94t1xyi2" />
                  </span>
                  {label}
                  <Check size={11} strokeWidth={3} className="shrink-0 text-emerald-500" data-id="element-936" data-mp="2ctfuxr2bfe3d" />
                </p>)}
            </div>

            <p className="text-[13.5px] leading-relaxed text-[#3D3229]" data-id="element-937" data-mp="28qji6gzgic7c">
              <strong className="font-semibold text-[#2B211A]" data-id="element-938" data-mp="o9tssbsgc12g">
                Three of eleven look at risk
              </strong>{' '}
              — $182k of $604k closing this quarter.
            </p>

            <ul className="divide-y divide-[#F1ECE3] overflow-hidden rounded-xl border border-[#EFEAE1] bg-[#FCFAF6]" data-id="element-939" data-mp="x9qbzxw2o7q4">
              {AT_RISK.map(r => <li key={r.account} className="flex items-start gap-3 px-3.5 py-2.5" data-id="element-940" data-mp="121zy12u2gnn4">
                  <span className="mt-[5px] size-1.5 shrink-0 rounded-full bg-red-400" data-id="element-941" data-mp="16z9wt93cibnm" />
                  <span className="min-w-0 flex-1" data-id="element-942" data-mp="1bxjsrlww9gaa">
                    <span className="block truncate text-[13px] font-semibold text-[#2B211A]" data-id="element-943" data-mp="1g0gf9npk1wji">
                      {r.account}
                    </span>
                    <span className="block truncate text-[11.5px] text-neutral-500" data-id="element-944" data-mp="1kz15cub3ezu8">
                      {r.reason}
                    </span>
                  </span>
                  <span className="shrink-0 text-right" data-id="element-945" data-mp="3mq4hxp4jmohf">
                    <span className="block text-[13px] font-semibold tabular-nums text-[#2B211A]" data-id="element-946" data-mp="3msr0skwvwqey">
                      {r.value}
                    </span>
                    <span className="block text-[11px] tabular-nums text-neutral-400" data-id="element-947" data-mp="oye4pc0ea7zj">
                      {r.closes}
                    </span>
                  </span>
                </li>)}
            </ul>

            <p className="text-[13px] text-neutral-500" data-id="element-948" data-mp="xx9aimzj1g2b">
              I've drafted outreach for each owner — want me to send it?
            </p>
            {/* Offering the next move as buttons is what makes this a working
             surface rather than a transcript of one. */}
            <div className="flex flex-wrap gap-2" data-id="element-949" data-mp="1lmk5fnu74g8j">
              <span className="rounded-lg bg-[#2B211A] px-3 py-1.5 text-[12px] font-semibold text-white" data-id="element-950" data-mp="1lpg3dc61n4wz">
                Send all three
              </span>
              <span className="rounded-lg border border-[#E7E0D4] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#2B211A]" data-id="element-951" data-mp="1vmkuzrinrsl6">
                Review drafts
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Composer */}
      <div className="shrink-0 px-5 pb-5 md:px-7" data-id="element-952" data-mp="2f7b8hzvhbew6">
        <div className="flex items-center gap-2.5 rounded-2xl border border-[#E7E0D4] bg-white px-3.5 py-2.5 shadow-[0_1px_2px_rgba(43,33,26,0.05)]" data-id="element-953" data-mp="2f72ycfqqqnoo">
          <Plus size={16} strokeWidth={1.9} className="shrink-0 text-neutral-400" data-id="element-954" data-mp="258vt2u5tdu2i" />
          <span className="min-w-0 flex-1 truncate text-[13px] text-neutral-400" data-id="element-955" data-mp="1ri0hha3xkyv8">
            Ask anything across your connected tools
          </span>
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#2B211A]" data-id="element-956" data-mp="1w9ifbpanqjbz">
            <ArrowUp size={13} strokeWidth={2.6} className="text-white" data-id="element-957" data-mp="1hldyyrz2ujwd" />
          </span>
        </div>
      </div>
    </div>;
}

// Named after the departments any company would recognise. The previous set
// ("Giga Master", "NoContact Ops") was Giga's own internal workspace, so a
// visitor had to decode what each one was before the permissions idea landed.
//
// Counts were the wrong content here: "14 tracks · 96 chats" says a group has
// stuff in it, which nobody doubted, and says nothing about what the screen is
// actually for. What a group IS, is a set of people plus the tools and work
// they can reach — so each card names those instead.
// ACCESS IS THE VARIABLE, so it has to vary. Every row carrying an identical
// "Members only" pill said nothing — a column where every cell reads the same
// is not information. Naming how each group is JOINED (invite only, by
// request, open to all) is the fact the screen exists to show, and the counts
// beside the name give each group a size so they read as real places rather
// than as four labels.
type GroupAccess = 'invite' | 'request' | 'open';
const GROUPS: Array<{
  name: string;
  color: string;
  access: GroupAccess;
  people: number;
  tracks: number;
  chats: number;
}> = [{
  name: 'Leadership',
  color: '#1E3A5F',
  access: 'invite',
  people: 5,
  tracks: 14,
  chats: 96
}, {
  name: 'Sales',
  color: '#134E4A',
  access: 'request',
  people: 7,
  tracks: 33,
  chats: 214
}, {
  name: 'Marketing',
  color: '#7C2D12',
  access: 'open',
  people: 6,
  tracks: 12,
  chats: 88
}, {
  name: 'Customer Success',
  color: '#3F3A2E',
  access: 'request',
  people: 4,
  tracks: 21,
  chats: 131
}];
const ACCESS: Record<GroupAccess, {
  label: string;
  icon: typeof Lock;
  className: string;
  iconClass: string;
}> = {
  invite: {
    label: 'Invite only',
    icon: Lock,
    className: 'border-[#EFEAE1] bg-[#FBF8F3] text-neutral-600',
    iconClass: 'text-neutral-400'
  },
  request: {
    label: 'By request',
    icon: UserPlus,
    className: 'border-[#EFEAE1] bg-[#FBF8F3] text-neutral-600',
    iconClass: 'text-neutral-400'
  },
  // The only open group is the only coloured pill — the exception is the thing
  // an admin scans this list for.
  open: {
    label: 'Open to all',
    icon: Users,
    className: 'border-[#F3DECB] bg-[#FFF6EF] text-[#C2410C]',
    iconClass: 'text-[#EA580C]'
  }
};
export function SharedScreen() {
  return <div data-id="element-958" data-mp="2rikf65vyic7s">
      <ScreenHead title="Groups & Permissions" meta="Who can see what — members get every track, chat and connection inside" data-id="element-959" data-mp="2mhobntfios2w" />
      {/* ONE COLUMN, not two. Access is a column of values you compare down the
       list; in a 2×2 grid there is no column to run your eye down. */}
      <ul className="space-y-2.5" data-id="element-960" data-mp="2wdrcmvymuko9">
        {GROUPS.map(g => {
        const access = ACCESS[g.access];
        const AccessIcon = access.icon;
        return <li key={g.name} className="flex items-center gap-3.5 rounded-2xl border border-[#EFEAE1] bg-[#FCFAF6] p-3.5 sm:gap-4 sm:p-4" data-id="element-961" data-mp="23ry9gl9fd40y">
              {/* A glyph, not initials. Two letters in a coloured tile read as
               somebody's avatar, and a group is not a person — the people icon
               says "several of us" at a glance. Squared off, where a person's
               avatar is round, so the two never get confused. */}
              <span className="flex size-11 shrink-0 items-center justify-center rounded-xl shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" style={{
            backgroundColor: g.color
          }} data-id="element-962" data-mp="1kyku3oi7366g">
                <Users size={18} strokeWidth={2} className="text-white" data-id="element-963" data-mp="3mq4kqoz7rmes" />
              </span>

              <div className="min-w-0 flex-1" data-id="element-964" data-mp="3roo7grlmt287">
                <p className="truncate text-[15px] font-bold leading-tight text-[#2B211A]" data-id="element-965" data-mp="3rph5932m4he7">
                  {g.name}
                </p>
                <p className="mt-1 truncate text-[12.5px] tabular-nums text-neutral-500" data-id="element-966" data-mp="o87burym2nr3">
                  {g.people} people <span className="text-neutral-300" data-id="element-967" data-mp="a8pugxnnacrr">·</span>{' '}
                  {g.tracks} tracks <span className="text-neutral-300" data-id="element-968" data-mp="aasg75hrljqo">·</span>{' '}
                  {g.chats} chats
                </p>
              </div>

              <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold ${access.className}`} data-id="element-969" data-mp="2uyf671t1xig8">
                <AccessIcon size={12} strokeWidth={2.2} className={access.iconClass} data-id="element-970" data-mp="2yxxij96t4l7g" />
                {access.label}
              </span>
            </li>;
      })}
      </ul>
    </div>;
}
const OWNER_AVATAR = 'https://cdn.magicpatterns.com/patterns/generated-images/b394cd62-6cbf-40a2-98a1-4af4a23e89dd.jpg';

// Every step names the tools it touches — a step with no marks left a visible
// gap in the column and read as a missing image rather than a deliberate blank.
const STEP_TOOLS: string[][] = [['jira', 'linear'], ['intercom', 'salesforce'], ['notion'], ['slack', 'gmail']];
export function WorkflowsScreen() {
  const steps = ['Pull what shipped, slipped and stalled from Jira and Linear', 'Check every account flagged in support or gone quiet', 'Draft the update into the team space — wins, risks, decisions', 'Post to #team-update and email the leadership list'];
  return <div data-id="element-971" data-mp="wy8x4trzppt5">
      {/* Name, then one line of what it produces, then the health of it as
       tags on the right. You should not have to read the steps to know why
       this workflow exists. */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-x-6 gap-y-4 border-b border-[#F1ECE3] pb-5" data-id="element-972" data-mp="zix88t4t1ewe">
        <div className="min-w-0" data-id="element-973" data-mp="14ijl31gq0t4o">
          <h3 className="font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-974" data-mp="19f0h0hu5wbsb">
            Monday morning update
          </h3>
          <p className="mt-1.5 flex items-center gap-1.5 text-[13px] text-[#6B5D52]" data-id="element-975" data-mp="1df0ykbck533l">
            Every Monday, 08:00
            <span className="text-[#D8CFC2]" data-id="element-976" data-mp="1nfb73inndmcf">·</span>
            <BrandLogo slug="slack" className="size-3.5" data-id="element-977" data-mp="1ndqehykw8dh7" />
            <span className="font-medium text-[#2B211A]" data-id="element-978" data-mp="1s5hwhkpzhavq">#team-update</span>
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-979" data-mp="1x5mc78j2i1kw">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600/20 bg-emerald-50 px-2.5 py-1.5 text-[11.5px] font-semibold text-emerald-700" data-id="element-980" data-mp="1x6xgrakc7dd2">
            <span className="size-1.5 rounded-full bg-emerald-500" data-id="element-981" data-mp="2662e4dk5x0nn" />
            Active
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1.5 text-[11.5px] font-semibold tabular-nums text-neutral-600" data-id="element-982" data-mp="374hkmmu5dr14">
            <Repeat size={11} className="text-neutral-400" data-id="element-983" data-mp="3g1jj0gaueauu" />
            26 runs
          </span>
          {/* The bar earns its place: 96% means more with 25 of 26 ticks
           filled beside it than as a number on its own. */}
          <span className="inline-flex items-center gap-2 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1.5 text-[11.5px] font-semibold tabular-nums text-neutral-600" data-id="element-984" data-mp="3pzg1xua0ua3i">
            <span className="flex items-end gap-[2px]" data-id="element-985" data-mp="3mhivpf6qhvy">
              {Array.from({
              length: 8
            }, (_, i) => <span key={i} className={`h-2.5 w-[2.5px] rounded-full ${i === 5 ? 'bg-red-400' : 'bg-emerald-500/80'}`} data-id="element-986" data-mp="7p4437hcp4k6" />)}
            </span>
            96%
          </span>
        </div>
      </div>

      {/* Who owns it and who can see it — the questions the steps can't
       answer. The read/write strip that sat here only restated them. */}
      <div className="mb-6 flex flex-wrap items-center gap-x-7 gap-y-3" data-id="element-987" data-mp="mp9luauboeng">
        <span className="flex items-center gap-2.5" data-id="element-988" data-mp="hqyatwsajy3n">
          <img src={OWNER_AVATAR} alt="" aria-hidden="true" className="size-7 shrink-0 rounded-full object-cover shadow-[0_0_0_1px_rgba(43,33,26,0.08)]" data-id="element-989" data-mp="hrhwnol2b6my" />
          <span data-id="element-990" data-mp="1agf4syvk7n1z">
            <span className="block text-[10px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-991" data-mp="1aeu750dy7nzw">
              Owner
            </span>
            <span className="block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-992" data-mp="15j53akuwbkxs">
              Sarah Chen
            </span>
          </span>
        </span>
        {/* "Shared with" read as the audience for the update — but the update
         goes to #team-update, which the subtitle already says. The useful fact
         is permission: who can run and edit this workflow. Same group tile the
         Groups screen uses, so a group reads as one object everywhere. */}
        <span className="flex items-center gap-2.5" data-id="element-993" data-mp="3io0pib68bbiv">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-xl bg-[#1E3A5F] shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" data-id="element-994" data-mp="3nmby6iksffzi">
            <Users size={13} strokeWidth={2} className="text-white" data-id="element-995" data-mp="38vuvkvwf2k11" />
          </span>
          <span data-id="element-996" data-mp="3dupd7uine2jk">
            <span className="block text-[10px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-997" data-mp="2zx0vgvjvnbiq">
              Can run and edit
            </span>
            <span className="block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-998" data-mp="34wvob4n7qb6f">
              Leadership · 5 people
            </span>
          </span>
        </span>
      </div>

      <p className="mb-2.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-neutral-400" data-id="element-999" data-mp="2ux4ruxf3dm3p">
        Steps
      </p>
      {/* A rail behind the numbers makes the steps read as one sequence
       rather than four unrelated cards. */}
      <ol className="relative space-y-2.5 before:absolute before:bottom-7 before:left-[30px] before:top-7 before:w-px before:bg-[#EFEAE1] before:content-['']" data-id="element-1000" data-mp="t5rv4vna2kiq">
        {steps.map((s, i) => <li key={s} className={`relative flex items-center gap-3.5 px-4 py-3.5 ${ROW}`} data-id="element-1001" data-mp="28r3oghw56ps1" data-mpi={i}>
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#2B211A] text-[11px] font-bold tabular-nums text-white shadow-[0_1px_2px_rgba(43,33,26,0.3)]" data-id="element-1002" data-mp="2nf6tnp2d1xfs">
              {i + 1}
            </span>
            <span className="flex-1 text-[13px] leading-snug text-[#3D3229]" data-id="element-1003" data-mp="2wh6cmmwxrbld">
              {s}
            </span>
            {/* The tools each step touches, stated once where they're used
             rather than duplicated in a strip above the list. */}
            <span className="flex shrink-0 items-center gap-1.5" data-id="element-1004" data-mp="31f7xnddx9kzh">
              {(STEP_TOOLS[i] ?? []).map(t => <BrandLogo key={t} slug={t} className="size-[15px] opacity-80" data-id="element-1005" data-mp="1c0b43826ebkg" />)}
            </span>
          </li>)}
      </ol>
      {/* Run now is the screen's primary action and was sized like a tag.
       It now matches the buttons used elsewhere in the product screens. */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#EFEAE1] pt-4" data-id="element-1006" data-mp="1dfw0nvsw4flm">
        <span className="inline-flex items-center gap-2 rounded-xl bg-[#2B211A] px-4 py-2.5 text-[13px] font-bold text-white shadow-[0_1px_2px_rgba(43,33,26,0.3),0_8px_18px_-10px_rgba(43,33,26,0.7)]" data-id="element-1007" data-mp="1if8lkb07bclk">
          <Play size={13} fill="currentColor" data-id="element-1008" data-mp="prma6gkqf3o5" />
          Run now
        </span>
        <span className="text-[12px] tabular-nums text-neutral-500" data-id="element-1009" data-mp="upo0nmkhm59n">
          Last run Monday 09:00 · 1m 12s
        </span>
      </div>
    </div>;
}
export function PulsesScreen() {
  // A pulse only matters if you know where its output lands, so the
  // destination sits on the row with the mark of the tool it posts to.
  const pulses = [{
    name: 'Monday revenue brief',
    cadence: 'Mondays, 08:00',
    next: 'in 2 days',
    to: '#revenue',
    logo: 'slack',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Ad spend anomaly check',
    cadence: 'Daily, 07:30',
    next: 'in 14 hours',
    to: 'Leadership list',
    logo: 'gmail',
    health: [1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 2]
  }, {
    name: 'Support escalation sweep',
    cadence: 'Every 4 hours',
    next: 'in 51 minutes',
    to: '#support-escalations',
    logo: 'slack',
    // Running right now. State belongs on the thing it describes, not in a
    // banner underneath that made you match a name back to a row.
    running: 'Step 2 of 4 · reading Intercom',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Churn signal digest',
    cadence: 'Fridays, 16:00',
    next: 'in 6 days',
    to: 'Customer success',
    logo: 'microsoftteams',
    health: [1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1]
  }];
  // One starts paused, so the control reads as a real state rather than a
  // decorative button — and the paused row visibly stops reporting.
  const [paused, setPaused] = React.useState<Record<string, boolean>>({
    'Churn signal digest': true
  });
  return <div data-id="element-1010" data-mp="18zq7t9l3hgot">
      {/* The subtitle defines the primitive rather than counting rows: someone
       meeting Pulses for the first time needs to know what one is, and the
       list below already shows how many are running. */}
      <ScreenHead title="Pulses" meta="Work that runs on a schedule and reports into the channel that needs it" data-id="element-1011" data-mp="13yakfavg2y39" />
      <ul className="space-y-2.5" data-id="element-1012" data-mp="1rnmsu8iy724i">
        {pulses.map(p => {
        const off = !!paused[p.name];
        const running = !off && 'running' in p ? (p as {
          running: string;
        }).running : null;
        return <li key={p.name} className={`flex flex-wrap items-center gap-3.5 px-4 py-3.5 ${ROW} ${running ? 'border-[#EA580C]/25 bg-[#FFF8F3]' : ''}`} data-id="element-1013" data-mp="1iurwy5fdeire">
              <span className="min-w-0 flex-1" data-id="element-1014" data-mp="2an0p4x45goy6">
                <span className={`block truncate text-[13.5px] font-semibold ${off ? 'text-[#8C7F73]' : 'text-[#2B211A]'}`} data-id="element-1015" data-mp="2am7ehl22lfar">
                  {p.name}
                </span>
                {/* While a pulse is mid-run, the row reports the run instead of
                 the schedule — the schedule is not the useful fact right now. */}
                {running ? <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] font-medium text-[#C2410C]" data-id="element-1016" data-mp="dojwcgyxy4bu">
                    <Loader2 size={11} className="shrink-0 animate-spin" data-id="element-1017" data-mp="8pqj8pnkgfc7" />
                    <span className="truncate" data-id="element-1018" data-mp="8rt5cylllobn">{running}</span>
                  </span> : <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-1019" data-mp="3h5sbgt2aaq2v">
                    {p.cadence}
                    <span className="text-[#D8CFC2]" data-id="element-1020" data-mp="3c8jsiq9bai0k">·</span>
                    <BrandLogo slug={p.logo} className={`size-3.5 ${off ? 'opacity-40' : ''}`} data-id="element-1021" data-mp="3r47vkc2w8z2v" />
                    <span className="truncate" data-id="element-1022" data-mp="3m3k4xrikuk96">{p.to}</span>
                  </span>}
              </span>
              {/* Twelve ticks say "is this healthy" faster than a percentage. */}
              <span className="flex shrink-0 items-end gap-[3px] rounded-md bg-white px-1.5 py-1 ring-1 ring-[#F1ECE3]" data-id="element-1023" data-mp="11d2u42kd6ypo">
                {p.health.map((h, i) => <span key={i} className={`h-3.5 w-[3px] rounded-full ${off ? 'bg-[#E2DACE]' : h === 1 ? 'bg-emerald-500/80' : h === 0 ? 'bg-red-500/85' : 'bg-amber-500/85'}`} data-id="element-1024" data-mp="2uv0didmuroiu" data-mpi={i} />)}
              </span>
              <span className="flex w-[92px] shrink-0 items-center justify-end gap-1.5 text-[11.5px] font-medium tabular-nums text-neutral-500" data-id="element-1025" data-mp="34t7s4pto5uho">
                {off ? <span className="text-neutral-400" data-id="element-1026" data-mp="3ejspyb1iquzj">Paused</span> : running ? <span className="font-semibold text-[#C2410C]" data-id="element-1027" data-mp="3imfjqhhr2pzd">Running</span> : <>
                    <Clock size={11} className="text-neutral-300" data-id="element-1028" data-mp="3ioa6m1cl0s63" />
                    {p.next}
                  </>}
              </span>
              <button type="button" onClick={() => setPaused(s => ({
            ...s,
            [p.name]: !off
          }))} aria-label={off ? `Resume ${p.name}` : `Pause ${p.name}`} className="flex size-7 shrink-0 items-center justify-center rounded-lg border border-[#EFEAE1] bg-white text-[#6B5D52] transition-colors duration-150 ease-out hover:border-[#D8CFC2] hover:text-[#2B211A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C]/40" data-id="element-1029" data-mp="3skvcsq0c89bi">
                {off ? <Play size={12} fill="currentColor" data-id="element-1030" data-mp="1gx9pbt75ds06" /> : <Pause size={12} fill="currentColor" data-id="element-1031" data-mp="1gybbottm60x7" />}
              </button>
            </li>;
      })}
      </ul>
    </div>;
}

// One sparkle on every row said "AI feature" four times and nothing about the
// skills themselves. A glyph per subject makes the list scannable by what each
// method is for, and the ink tile matches the group tiles on the Groups screen
// so both screens read as the same application.
export function SkillsScreen() {
  // Named as the piece of knowledge itself ("Inbound lead qualification")
  // rather than as a sentence about it ("How we qualify an inbound lead") —
  // a library of nouns scans; a list of sentences has to be read.
  const skills = [{
    name: 'Inbound lead qualification',
    icon: Filter,
    used: 142,
    group: 'Marketing',
    by: 'Priya Nair',
    initials: 'PN',
    color: '#7C2D12',
    created: 'Jun 4'
  }, {
    name: 'Weekly reporting format',
    icon: BarChart3,
    used: 96,
    group: 'Leadership',
    by: 'Sarah Chen',
    initials: 'SC',
    color: '#1E3A5F',
    created: 'May 12'
  }, {
    name: 'P1 incident escalation',
    icon: Siren,
    used: 38,
    group: 'Customer Success',
    by: 'Tom Alvarez',
    initials: 'TA',
    color: '#3F3A2E',
    created: 'Apr 28'
  }, {
    name: 'Renewal email drafting',
    icon: Mail,
    used: 211,
    group: 'Sales',
    by: 'Marcus Lee',
    initials: 'ML',
    color: '#134E4A',
    created: 'Mar 19'
  }];
  return <div data-id="element-1032" data-mp="a4g6zscmdpxd">
      <ScreenHead title="Skills" meta="How your company does something, written once and reused everywhere" data-id="element-1033" data-mp="a46g82jr3eqm" />
      <ul className="space-y-2.5" data-id="element-1034" data-mp="2bxw1j0btvoxm">
        {skills.map(s => <li key={s.name} className={`flex items-center gap-3.5 px-4 py-3.5 ${ROW}`} data-id="element-1035" data-mp="2byfiadpqs7a2">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#2B211A] shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" data-id="element-1036" data-mp="27048u43czysd">
              <s.icon size={15} strokeWidth={1.9} className="text-white" data-id="element-1037" data-mp="rg4pdteg32v6" />
            </span>
            <span className="min-w-0 flex-1" data-id="element-1038" data-mp="crgt1kwryp46">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-1039" data-mp="cqp1qma7ndjp">
                {s.name}
              </span>
              {/* Who wrote the method, and which group it belongs to. A skill
               is somebody's expertise made reusable, so the person is the
               credential — freshness matters far less than authorship here. */}
              <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-1040" data-mp="3pyik4z469rzz">
                <span className="flex size-[17px] shrink-0 items-center justify-center rounded-full text-[7px] font-bold text-white" style={{
              backgroundColor: s.color
            }} data-id="element-1041" data-mp="3uxmy0321kdwn">
                  {s.initials}
                </span>
                <span className="truncate text-[#6B5D52]" data-id="element-1042" data-mp="1ro1obueqgd3l">{s.by}</span>
                <span className="text-[#D8CFC2]" data-id="element-1043" data-mp="1rpcqxqwfzet4">·</span>
                <span className="truncate" data-id="element-1044" data-mp="1mop2nczllc2l">{s.group}</span>
                <span className="text-[#D8CFC2]" data-id="element-1045" data-mp="1mrb9faenznjr">·</span>
                <span className="shrink-0 whitespace-nowrap" data-id="element-1046" data-mp="1hqx2jvosujnb">{s.created}</span>
              </span>
            </span>
            {/* Reuse count as a stat, not a badge: the number is the point, so
             it carries the weight and the unit sits under it in small caps. */}
            <span className="shrink-0 text-right" data-id="element-1047" data-mp="25ewrcq2jvzz9">
              <span className="block text-[15px] font-semibold tabular-nums leading-none text-[#2B211A]" data-id="element-1048" data-mp="25hj7vf7f5ptm">
                {s.used}
              </span>
              <span className="mt-1 block text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-1049" data-mp="1vqetohpki8zu">
                uses
              </span>
            </span>
          </li>)}
      </ul>
    </div>;
}